import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { usePlatform } from "@/context/PlatformContext";
import { useAuth } from "@/context/AuthContext";
import { devWarn } from "@/lib/logger";
import { LanderDomainCard } from "@/components/settings/LanderDomainCard";
import { VisitorIpCheck } from "@/components/settings/VisitorIpCheck";
import { US_ZONES } from "@/components/buyersetup/AcceptHours";

// Runtime platform settings — super_admin only. Lives at /settings/platform.
// Currently exposes:
//   • Public status page toggle + custom headline/subhead
//   • Support email (surfaced on /status when public)
//   • Global MFA kill-switch
// Env-baked values (name/tagline/domain) are read-only here — those live in
// backend/.env and change on deploy.

export default function PlatformSettings() {
  const boot = usePlatform();
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  // Iter-63.1 — emergency pause dialog state (super_admin only).
  const [pauseDialog, setPauseDialog] = useState(null);   // {intent:"pause"|"resume", reason:""}
  const [pausing, setPausing] = useState(false);

  useEffect(() => {
    // Re-fetch from server so we get the runtime override (context only has
    // the un-authed shape). Intentionally runs once — `boot` from context is
    // static after mount.
    api.get("/platform")
      .then((r) => setS({ ...boot, ...r.data }))
      .catch((err) => {
        devWarn("Failed to load runtime platform settings:", err?.message || err);
        setS(boot);
      });
  }, []);

  const set = (k, v) => setS((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const patch = {
        show_public_status_page: !!s.show_public_status_page,
        status_page_headline: s.status_page_headline || "",
        status_page_subhead: s.status_page_subhead || "",
        support_email: s.support_email || "",
        mfa_globally_disabled: !!s.mfa_globally_disabled,
        default_lead_price: Number(s.default_lead_price) || 0,
        // Iter-53: number-visibility toggles
        hide_phone_displays_global: !!s.hide_phone_displays_global,
        hide_admin_numbers_global: !!s.hide_admin_numbers_global,
        buyer_hours_timezone: s.buyer_hours_timezone || "America/Chicago",
      };
      const { data } = await api.patch("/platform", patch);
      setS({ ...s, ...data });
      if (boot?.refresh) boot.refresh();
      toast.success("Platform settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  // Iter-63.1 — Emergency delivery pause toggler. Separate confirm dialog
  // so it can't be flipped as part of the generic Save button above.
  const submitEmergencyPause = async () => {
    if (!pauseDialog) return;
    setPausing(true);
    try {
      const patch = pauseDialog.intent === "pause"
        ? {
            delivery_paused_globally: true,
            delivery_paused_note: pauseDialog.reason || "",
            delivery_paused_at: new Date().toISOString(),
            delivery_paused_by: user?.email || user?.id || "",
          }
        : {
            delivery_paused_globally: false,
            delivery_paused_note: "",
            delivery_paused_at: "",
            delivery_paused_by: "",
          };
      const { data } = await api.patch("/platform", patch);
      setS({ ...s, ...data });
      if (boot?.refresh) boot.refresh();
      toast.success(pauseDialog.intent === "pause"
        ? "Emergency delivery pause ACTIVATED — new production deliveries halted"
        : "Emergency pause resumed — normal delivery restored");
      setPauseDialog(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setPausing(false);
    }
  };

  if (!s) return <div className="label-mono">Loading…</div>;

  return (
    <div className="max-w-3xl">
      <div className="label-mono">Admin</div>
      <h1 className="font-chivo font-black text-4xl uppercase tracking-tighter mb-6">Platform settings</h1>

      {/* Read-only branding */}
      <div className="brut-card p-6 mb-4">
        <div className="font-chivo font-bold uppercase tracking-tight mb-3">Branding <span className="text-[10px] text-zinc-500 font-mono ml-2">(env-baked · edit in backend/.env)</span></div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div><div className="label-mono">Name</div><div className="font-mono">{s.name}</div></div>
          <div><div className="label-mono">Short name</div><div className="font-mono">{s.short_name}</div></div>
          <div><div className="label-mono">Tagline</div><div className="font-mono">{s.tagline}</div></div>
          <div><div className="label-mono">Domain</div><div className="font-mono">{s.domain}</div></div>
        </div>
      </div>

      {/* Public status toggle */}
      <div className="brut-card p-6 mb-4" data-testid="settings-status-block">
        <div className="font-chivo font-bold uppercase tracking-tight mb-2">Public status page</div>
        <label className="inline-flex items-center gap-2 mb-4">
          <input type="checkbox" className="h-4 w-4 border-2 border-black"
            data-testid="settings-show-status-toggle"
            checked={!!s.show_public_status_page}
            onChange={(e) => set("show_public_status_page", e.target.checked)} />
          <span className="text-sm">Publish <code className="font-mono">/status</code> to the public web</span>
        </label>
        <div className="text-xs text-zinc-600 mb-4">
          When ON, anyone can visit <code className="font-mono">/status</code> to see delivery uptime + volume KPIs — no login required.
          When OFF, the endpoint returns 404 and doesn&apos;t leak that it exists.
        </div>
        <div className="grid grid-cols-1 gap-3">
          <div>
            <label className="label-mono">Custom headline (optional)</label>
            <input data-testid="settings-status-headline"
              className="w-full border border-black px-3 py-2 mt-1 font-mono"
              placeholder="All systems operational"
              value={s.status_page_headline || ""}
              onChange={(e) => set("status_page_headline", e.target.value)} />
          </div>
          <div>
            <label className="label-mono">Sub-headline (optional)</label>
            <input data-testid="settings-status-subhead"
              className="w-full border border-black px-3 py-2 mt-1 font-mono"
              placeholder="Anything else you want visitors to see"
              value={s.status_page_subhead || ""}
              onChange={(e) => set("status_page_subhead", e.target.value)} />
          </div>
          <div>
            <label className="label-mono">Public support email</label>
            <input data-testid="settings-support-email"
              className="w-full border border-black px-3 py-2 mt-1 font-mono"
              placeholder="support@yourdomain.com"
              value={s.support_email || ""}
              onChange={(e) => set("support_email", e.target.value)} />
            <div className="mt-1 text-[10px] text-zinc-500">
              Shown on <code className="font-mono">/status</code> as a &ldquo;Contact support&rdquo; button. Leave blank to omit.
            </div>
          </div>
        </div>
      </div>

      {/* Iter-30.5: Default $ per lead — auto-fills state_fees on new destinations */}
      <div className="brut-card p-6 mb-4" data-testid="settings-price-block">
        <div className="font-chivo font-bold uppercase tracking-tight mb-2">Default price per lead</div>
        <label className="label-mono">Starting $/lead for every new destination</label>
        <div className="flex items-center gap-2 mt-1">
          <span className="font-mono text-lg">$</span>
          <input
            type="number"
            min="0"
            step="0.01"
            data-testid="settings-default-lead-price"
            className="w-40 border border-black px-3 py-2 font-mono"
            value={s.default_lead_price ?? 350}
            onChange={(e) => set("default_lead_price", parseFloat(e.target.value) || 0)}
          />
          <span className="text-xs text-zinc-500">/ lead</span>
        </div>
        <div className="text-xs text-zinc-600 mt-2 leading-relaxed">
          When you create a new delivery destination and don&apos;t set per-state fees, every
          state in the allowlist is auto-priced at this value. Individual destinations
          can still override per state (e.g. AL:$350, GA:$200) in the destination form.
        </div>
      </div>

      {/* Buyer lead hours default time zone */}
      <div className="brut-card p-6 mb-4" data-testid="settings-hours-block">
        <div className="font-chivo font-bold uppercase tracking-tight mb-2">Buyer lead hours</div>
        <label className="label-mono">Time zone for every buyer&apos;s hours</label>
        <select data-testid="settings-hours-timezone" className="w-64 border border-black px-3 py-2 mt-1 font-mono block"
          value={s.buyer_hours_timezone || "America/Chicago"} onChange={(e) => set("buyer_hours_timezone", e.target.value)}>
          {US_ZONES.map(([z, n]) => <option key={z} value={z}>{n}</option>)}
        </select>
        <div className="text-xs text-zinc-600 mt-2 leading-relaxed">
          Every buyer takes leads 24/7 unless you set their days and hours on the buyer. Hours use this time zone
          unless that buyer picks their own.
        </div>
      </div>

      {/* MFA kill-switch */}
      <div className="brut-card p-6 mb-4">
        <div className="font-chivo font-bold uppercase tracking-tight mb-2">Multi-factor auth</div>
        <label className="inline-flex items-center gap-2">
          <input type="checkbox" className="h-4 w-4 border-2 border-black"
            data-testid="settings-mfa-disabled-toggle"
            checked={!!s.mfa_globally_disabled}
            onChange={(e) => set("mfa_globally_disabled", e.target.checked)} />
          <span className="text-sm">Disable MFA globally (skip TOTP prompt on every sign-in)</span>
        </label>
        <div className="text-xs text-zinc-600 mt-2">
          When ON, MFA is <b>optional / paused</b> — users who&apos;ve enrolled won&apos;t be prompted for a code and enforcement policies are ignored. Individual enrollments are preserved so flipping this back OFF resumes the challenge.
        </div>
      </div>

      {/* Iter-53: Number visibility (public + admin) */}
      <div className="brut-card p-6 mb-4" data-testid="settings-numbers-block">
        <div className="font-chivo font-bold uppercase tracking-tight mb-2">Number visibility</div>
        <div className="text-xs text-zinc-600 mb-4 leading-relaxed">
          Two independent kill-switches for phone / price displays. Turn either
          ON while migrating a phone number or before a screen-share, then flip
          back OFF once you&apos;re ready. Nothing is deleted — the underlying
          numbers stay in the database so you can edit or re-enable them any
          time.
        </div>

        <label className="flex items-start gap-3 mb-4 cursor-pointer">
          <input type="checkbox" className="mt-1 h-4 w-4 border-2 border-black"
            data-testid="settings-hide-phone-global-toggle"
            checked={!!s.hide_phone_displays_global}
            onChange={(e) => set("hide_phone_displays_global", e.target.checked)} />
          <span className="text-sm">
            <div className="font-bold">Hide phone displays on all landing pages</div>
            <div className="text-xs text-zinc-600 mt-1 leading-relaxed">
              Removes the top &ldquo;Call 1-800…&rdquo; bar, the hero call CTA, the
              Thank-You &ldquo;Call our team&rdquo; button, the footer phone link,
              and the sticky mobile call bar — everywhere, on every published
              landing page. The visitor&apos;s phone-number <em>input</em> field
              on the intake form stays exactly as-is so lead capture is
              unaffected.
            </div>
          </span>
        </label>

        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" className="mt-1 h-4 w-4 border-2 border-black"
            data-testid="settings-hide-admin-numbers-toggle"
            checked={!!s.hide_admin_numbers_global}
            onChange={(e) => set("hide_admin_numbers_global", e.target.checked)} />
          <span className="text-sm">
            <div className="font-bold">Screen-share mode (mask numbers in admin lists)</div>
            <div className="text-xs text-zinc-600 mt-1 leading-relaxed">
              Masks the consumer phone column on the <code className="font-mono">/leads</code>
              {" "}table and the phone chip on the Custom Landing Pages list with{" "}
              <code className="font-mono">•••</code> so you can share your screen
              with a buyer or affiliate without leaking data. Detail pages still
              show real values.
            </div>
          </span>
        </label>
      </div>

      <button onClick={save} disabled={busy} data-testid="settings-save"
        className="bg-black text-white border border-black px-4 py-2 uppercase text-xs tracking-widest font-bold hover:translate-x-[2px] hover:translate-y-[2px] hover:brut-shadow-sm transition-transform duration-100 disabled:opacity-60">
        {busy ? "Saving…" : "Save settings"}
      </button>

      {/* Iter-63.1: Emergency delivery pause — super_admin only. */}
      {user?.role === "super_admin" && (
        <div className="brut-card p-6 mt-6 border-red-700 border-2 bg-red-50"
             data-testid="settings-emergency-pause-block">
          <div className="font-chivo font-black uppercase tracking-tight mb-2 text-red-900">
            Emergency delivery pause
          </div>
          <div className="text-xs text-zinc-700 mb-4 leading-relaxed">
            When ON, <code className="font-mono">dispatch_lead()</code>{" "}
            short-circuits BEFORE any destination is contacted. Leads still
            ingest and record; existing queued retries are untouched.
          </div>
          <div className="text-sm mb-4" data-testid="settings-emergency-pause-status">
            Current state:{" "}
            <span className={`font-bold ${s.delivery_paused_globally ? "text-red-800" : "text-emerald-800"}`}>
              {s.delivery_paused_globally ? "PAUSED" : "OPERATING NORMALLY"}
            </span>
            {s.delivery_paused_globally && (s.delivery_paused_at || s.delivery_paused_by || s.delivery_paused_note) && (
              <div className="text-[11px] text-zinc-600 mt-1">
                {s.delivery_paused_at && <>Since <b>{s.delivery_paused_at}</b> · </>}
                {s.delivery_paused_by && <>By <b>{s.delivery_paused_by}</b> · </>}
                {s.delivery_paused_note && <>Note: {s.delivery_paused_note}</>}
              </div>
            )}
          </div>
          {s.delivery_paused_globally ? (
            <button onClick={() => setPauseDialog({ intent: "resume", reason: "" })}
              data-testid="settings-emergency-resume-btn"
              className="border-2 border-emerald-700 bg-emerald-600 text-white px-3 py-2 text-xs uppercase tracking-widest hover:bg-emerald-700">
              Resume normal delivery
            </button>
          ) : (
            <button onClick={() => setPauseDialog({ intent: "pause", reason: "" })}
              data-testid="settings-emergency-pause-btn"
              className="border-2 border-red-700 bg-red-700 text-white px-3 py-2 text-xs uppercase tracking-widest hover:bg-red-800">
              Activate emergency pause
            </button>
          )}
          <div className="mt-3 text-[10px] text-zinc-600">
            Failed deliveries board:{" "}
            <Link to="/admin/failed-deliveries" data-testid="settings-link-failed-deliveries"
                  className="underline">/admin/failed-deliveries</Link>
          </div>
        </div>
      )}

      {["super_admin", "admin"].includes(user?.role) && user?.org_type === "internal" && <LanderDomainCard />}
      {["super_admin", "admin"].includes(user?.role) && user?.org_type === "internal" && <VisitorIpCheck />}

      {pauseDialog && (
        <div className="fixed inset-0 z-40 flex items-start justify-center pt-16 bg-black/50"
             data-testid="settings-emergency-modal">
          <div className="bg-white border-2 border-black brut-shadow-md max-w-lg w-full mx-4 p-5">
            <div className="font-chivo font-black uppercase tracking-tighter mb-3 text-red-900">
              {pauseDialog.intent === "pause"
                ? "Activate emergency delivery pause"
                : "Resume normal delivery"}
            </div>
            <div className="text-xs text-zinc-800 mb-3 leading-relaxed">
              {pauseDialog.intent === "pause"
                ? "This will HALT every new production delivery until you explicitly resume. Queued retries and past attempts are NOT deleted."
                : "This will resume normal production delivery. Any leads that were held while paused will NOT be automatically re-dispatched."}
            </div>
            {pauseDialog.intent === "pause" && (
              <>
                <label className="label-mono">Reason (audit annotation)</label>
                <textarea rows={3}
                  className="w-full border border-black px-2 py-1.5 mt-1 text-sm"
                  placeholder="e.g. Buyer intake pipeline reported an outage — pausing to prevent lost leads."
                  value={pauseDialog.reason}
                  onChange={(e) => setPauseDialog({ ...pauseDialog, reason: e.target.value })}
                  data-testid="settings-emergency-reason" />
              </>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setPauseDialog(null)} disabled={pausing}
                data-testid="settings-emergency-cancel"
                className="border border-black px-3 py-1.5 text-[11px] uppercase tracking-widest hover:bg-black hover:text-white">
                Cancel
              </button>
              <button onClick={submitEmergencyPause} disabled={pausing}
                data-testid="settings-emergency-confirm"
                className={`border px-3 py-1.5 text-[11px] uppercase tracking-widest disabled:opacity-40 ${
                  pauseDialog.intent === "pause"
                    ? "bg-red-700 border-red-700 text-white"
                    : "bg-emerald-700 border-emerald-700 text-white"
                }`}>
                {pausing ? "Applying…" : pauseDialog.intent === "pause" ? "Confirm pause" : "Confirm resume"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
